import { MODERATION_CATEGORY_LABELS, MODERATION_RULES, REPORT_REASONS, type AdminFlag, type AdminModerationView, type ModerationCategory } from '@trendator/shared';
import { useState } from 'react';
import { Link } from '../components/Layout';
import { Button, Card, Chip, Empty, ErrorBox, Field, FilterButton, Label, Loading, Modal, PageHead, Status, Switch, Textarea, useToast, type Tone } from '../components/ui';
import { api, errorText } from '../lib/api';
import { dateTime, num, shortId, userName, when } from '../lib/format';
import { useLoad } from '../lib/hooks';
import { setQuery } from '../lib/router';

const FILTERS = [
  ['open', 'На проверке'],
  ['confirmed', 'Подтверждены'],
  ['dismissed', 'Ложные'],
  ['all', 'Все'],
] as const;

const SOURCE: Record<AdminFlag['source'], string> = {
  prompt: 'Промпт',
  input: 'Фото на входе',
  output: 'Результат',
  report: 'Жалоба',
};

const ACTION: Record<AdminFlag['action'], { label: string; tone: Tone }> = {
  block: { label: 'Заблокировано', tone: 'bad' },
  flag: { label: 'Пропущено, под вопросом', tone: 'warn' },
  report: { label: 'Жалоба юзера', tone: 'warn' },
};

const STATUS: Record<AdminFlag['status'], { label: string; tone: Tone }> = {
  open: { label: 'Открыт', tone: 'warn' },
  confirmed: { label: 'Нарушение', tone: 'bad' },
  dismissed: { label: 'Ложное', tone: 'idle' },
};

const catLabel = (c: string) => MODERATION_CATEGORY_LABELS[c as ModerationCategory] ?? c;
const reportLabel = (r: string | null) => REPORT_REASONS.find((x) => x.id === r)?.label ?? r ?? '';

type Media = { key: string; url: string | null; video: boolean }[];

export function Moderation({ query }: { query: URLSearchParams }) {
  const toast = useToast();
  const filter = (FILTERS.find(([id]) => id === query.get('status'))?.[0] ?? 'open') as (typeof FILTERS)[number][0];
  const user = query.get('user');
  const qs = new URLSearchParams({ ...(filter === 'open' ? {} : { status: filter }), ...(user ? { userId: user } : {}) }).toString();
  const d = useLoad(() => api.get<AdminModerationView>(`/moderation${qs ? `?${qs}` : ''}`), [qs]);
  const [media, setMedia] = useState<Record<number, Media>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<AdminFlag | null>(null);
  const [opts, setOpts] = useState({ ban: false, deleteFiles: true, note: '' });

  const reveal = async (f: AdminFlag) => {
    setBusy(`reveal${f.id}`);
    try {
      const r = await api.post<{ items: Media }>(`/moderation/${f.id}/reveal`);
      setMedia((m) => ({ ...m, [f.id]: r.items }));
      toast('Просмотр записан в аудит');
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(null);
    }
  };

  const decide = async (f: AdminFlag, decision: 'confirmed' | 'dismissed') => {
    setBusy(`${decision}${f.id}`);
    try {
      const body = decision === 'confirmed' ? { decision, ban: opts.ban, deleteFiles: opts.deleteFiles, note: opts.note.trim() || undefined } : { decision };
      const r = await api.post<AdminFlag>(`/moderation/${f.id}/decide`, body);
      toast(decision === 'dismissed' ? 'Отмечено как ложное' : r.userBanned && opts.ban ? 'Нарушение подтверждено, юзер забанен' : 'Нарушение подтверждено');
      setConfirm(null);
      setMedia((m) => {
        const { [f.id]: _drop, ...rest } = m;
        return rest;
      });
      await d.reload();
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (enabled: boolean) => {
    try {
      await api.put('/settings/moderation', { enabled });
      toast(enabled ? 'Автомодерация включена' : 'Автомодерация выключена');
      await d.reload();
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  };

  if (d.error && !d.data) return <ErrorBox text={d.error} onRetry={() => void d.reload()} />;
  const v = d.data;

  return (
    <>
      <PageHead
        title="Модерация"
        actions={
          v ? (
            <label className="flex items-center gap-2.5 text-sm text-soft">
              Автомодерация
              <Switch checked={v.enabled} onChange={(x) => void toggle(x)} label="Автомодерация" />
            </label>
          ) : null
        }
      />

      {v && !v.modelConfigured ? (
        <Card className="border-warn/30 text-sm text-warn">Модель не подключена (QC_API_KEY / KIE_API_KEY) — работает только стоп-лист промптов и проверка возраста по лицу.</Card>
      ) : null}
      {v && !v.enabled ? <Card className="border-warn/30 text-sm text-warn">Автомодерация выключена: промпты, фото и результаты не проверяются. Жалобы юзеров приходят как обычно.</Card> : null}

      <div className="grid grid-cols-3 gap-3">
        <Stat label="На проверке" value={v ? num(v.counts.open) : '…'} tone={v?.counts.open ? 'warn' : undefined} />
        <Stat label="Жалоб юзеров" value={v ? num(v.counts.reports) : '…'} tone={v?.counts.reports ? 'bad' : undefined} />
        <Stat label="Автоблоков за сутки" value={v ? num(v.counts.blocked24h) : '…'} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {user ? (
          <FilterButton on onClick={() => setQuery({ user: null })}>
            юзер #{user} ×
          </FilterButton>
        ) : null}
        {FILTERS.map(([id, label]) => (
          <FilterButton key={id} on={filter === id} onClick={() => setQuery({ status: id === 'open' ? null : id })}>
            {label}
          </FilterButton>
        ))}
      </div>

      {!v ? <Loading /> : null}
      {v && v.items.length === 0 ? (
        <Card>
          <Empty>{filter === 'open' ? 'Очередь пуста — всё разобрано' : 'Пусто'}</Empty>
        </Card>
      ) : null}

      {v?.items.map((f) => (
        <Card key={f.id} className="flex flex-col gap-3" aria-label={`Флаг ${f.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold">{SOURCE[f.source]}</span>
            <Status tone={ACTION[f.action].tone}>{ACTION[f.action].label}</Status>
            {f.categories.map((c) => (
              <Chip key={c} tone="bad">
                {catLabel(c)}
              </Chip>
            ))}
            {f.status !== 'open' ? <Status tone={STATUS[f.status].tone}>{STATUS[f.status].label}</Status> : null}
            <span className="ml-auto text-xs text-muted" title={dateTime(f.createdAt)}>
              #{f.id} · {when(f.createdAt)}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <Link to={`/users/${f.user.id}`} className="font-semibold text-white">
              {userName(f.user)}
            </Link>
            <span className={f.strikes >= MODERATION_RULES.strikesWarn ? 'font-semibold text-bad' : 'text-muted'}>нарушений за 30 дней: {f.strikes}</span>
            {f.userBanned ? <Chip tone="bad">забанен</Chip> : null}
            {f.generationId ? (
              <Link to={`/generations?id=${f.generationId}`} className="font-mono text-xs text-muted">
                генерация #{shortId(f.generationId)}
              </Link>
            ) : null}
            {f.reporterId ? (
              <span className="text-muted">
                пожаловался{' '}
                <Link to={`/users/${f.reporterId}`} className="text-soft">
                  #{f.reporterId}
                </Link>
                {f.reportReason ? ` · ${reportLabel(f.reportReason)}` : ''}
              </span>
            ) : null}
          </div>

          {f.text ? <p className="m-0 rounded-[12px] bg-field px-3 py-2 text-sm break-words whitespace-pre-wrap text-soft">{f.text}</p> : null}
          {f.reason ? <span className="text-xs text-muted">Автоматика: {f.reason}</span> : null}

          {media[f.id] ? (
            <div className="flex flex-wrap gap-2">
              {media[f.id]!.map((m) =>
                m.url ? (
                  m.video ? (
                    <video key={m.key} src={m.url} controls className="h-48 rounded-[12px] bg-black" />
                  ) : (
                    <a key={m.key} href={m.url} target="_blank" rel="noreferrer">
                      <img src={m.url} alt="Файл на проверке" className="h-48 rounded-[12px] object-cover" />
                    </a>
                  )
                ) : null,
              )}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            {f.media > 0 && !media[f.id] ? (
              <Button size="sm" busy={busy === `reveal${f.id}`} onClick={() => void reveal(f)}>
                Показать файлы ({f.media})
              </Button>
            ) : null}
            {f.media === 0 && f.status === 'open' ? <Label>файлов нет{f.action === 'block' ? ' — заблокированное не храним' : ''}</Label> : null}
            {f.status === 'open' ? (
              <span className="ml-auto inline-flex gap-1.5">
                <Button size="sm" busy={busy === `dismissed${f.id}`} onClick={() => void decide(f, 'dismissed')}>
                  Ложное
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setOpts({ ban: f.strikes + 1 >= MODERATION_RULES.strikesWarn, deleteFiles: f.media > 0 || Boolean(f.generationId), note: '' });
                    setConfirm(f);
                  }}
                >
                  Подтвердить
                </Button>
              </span>
            ) : (
              <span className="ml-auto text-xs text-muted">
                {f.decidedBy}
                {f.note ? ` · ${f.note}` : ''}
              </span>
            )}
          </div>
        </Card>
      ))}

      <Modal open={confirm !== null} onClose={() => setConfirm(null)} title="Подтвердить нарушение">
        {confirm ? (
          <>
            <span className="text-sm text-soft">
              {userName(confirm.user)} · нарушений за 30 дней станет {confirm.strikes + 1}
              {confirm.strikes + 1 >= MODERATION_RULES.strikesWarn ? ' — пора банить' : ''}.
            </span>
            <label className="flex items-center justify-between gap-3 text-sm">
              Удалить файлы (результат, загруженное фото)
              <Switch checked={opts.deleteFiles} onChange={(x) => setOpts((o) => ({ ...o, deleteFiles: x }))} label="Удалить файлы" />
            </label>
            <label className="flex items-center justify-between gap-3 text-sm">
              Забанить юзера
              <Switch checked={opts.ban} onChange={(x) => setOpts((o) => ({ ...o, ban: x }))} label="Забанить юзера" />
            </label>
            <Field label="Заметка (необязательно)">
              <Textarea value={opts.note} maxLength={500} onChange={(e) => setOpts((o) => ({ ...o, note: e.target.value }))} className="min-h-16" />
            </Field>
            <Button variant="danger" busy={busy === `confirmed${confirm.id}`} onClick={() => void decide(confirm, 'confirmed')}>
              Подтвердить{opts.ban ? ' и забанить' : ''}
            </Button>
          </>
        ) : null}
      </Modal>
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' | 'bad' }) {
  return (
    <Card className="flex flex-col gap-1">
      <Label>{label}</Label>
      <b className={tone === 'warn' ? 'text-2xl text-warn' : tone === 'bad' ? 'text-2xl text-bad' : 'text-2xl'}>{value}</b>
    </Card>
  );
}
